import React from 'react';
import { Team } from '../../types';
import { formatSignedHours, getLoggedHoursDiffClasses, getBudgetVsActualColor, getBudgetVsActualColorBold } from '../../utils/colorHelpers';

interface ResourcePlanningData {
  team: string;
  billableRateThisWeek: number;
  billableRateNextWeek: number;
  billableRateIn2Weeks: number;
  billableRateIn3Weeks: number;
}

interface TeamKPIsWidgetProps {
  teams: Team[];
  employeesTable: any[];
  resourcePlanningData: ResourcePlanningData[];
  isLoading: boolean;
  onTeamSelect?: (teamName: string) => void;
  selectedTeams?: string[];
}

const averagePositiveValues = (values: any[]): number | null => {
  const validValues = values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (validValues.length === 0) {
    return null;
  }

  return validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
};

const getTeamBudgetRate = (teamEmployees: any[]) => {
  if (teamEmployees.length === 0) return null;
  const average = averagePositiveValues(teamEmployees.map((emp) => emp.billableRate));
  return average !== null ? Math.round(average) : null;
};

const getWeekColorClass = (resourceData: ResourcePlanningData | undefined, field: keyof ResourcePlanningData, teamEmployees: any[]) => {
  if (!resourceData) return 'text-gray-500';
  if (teamEmployees.length === 0) return 'text-gray-500';

  const validBudgetRates = teamEmployees.map((emp: any) => emp.billableRate).filter((rate: number) => !isNaN(rate) && rate > 0);
  if (validBudgetRates.length === 0) return 'text-gray-500';

  const avgBudgetRate = validBudgetRates.reduce((sum: number, rate: number) => sum + rate, 0) / validBudgetRates.length;
  const weekValue = resourceData[field] as number;

  if (weekValue > avgBudgetRate) return 'text-green-600';
  if (weekValue >= avgBudgetRate * 0.95) return 'text-yellow-600';
  return 'text-red-600';
};

const getBillableRateAOYColor = (teamEmployees: any[]) => {
  if (teamEmployees.length === 0) return '';
  const avgBillableRate = averagePositiveValues(teamEmployees.map((emp: any) => emp.billableRateAOY));
  if (avgBillableRate === null) return '';
  const avgBudgetRate = averagePositiveValues(teamEmployees.map((emp: any) => emp.billableRate));
  if (avgBudgetRate === null) return '';
  if (avgBillableRate > avgBudgetRate) return 'text-green-600';
  if (avgBillableRate >= avgBudgetRate * 0.95) return 'text-yellow-600';
  return 'text-red-600';
};

export const TeamKPIsWidget: React.FC<TeamKPIsWidgetProps> = ({
  teams,
  employeesTable,
  resourcePlanningData,
  isLoading,
  onTeamSelect,
  selectedTeams = [],
}) => {
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/40 overflow-hidden">
      <div className="px-8 py-6 border-b border-slate-200/40 bg-gradient-to-r from-slate-50/50 to-blue-50/50">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">Team KPIs</h3>
        <p className="text-slate-600 mt-1">Performance metrics across all teams</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 border-r-2 border-gray-300" style={{ width: '175px', minWidth: '175px', maxWidth: '175px' }}>Team</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50/50" colSpan={4}>
                <div className="text-center">Budget</div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50/50 border-l-2 border-gray-300" colSpan={4}>
                <div className="text-center">As of Yesterday</div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-purple-50/50 border-l-2 border-gray-300" colSpan={4}>
                <div className="text-center">Upcoming 4 weeks</div>
              </th>
            </tr>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 border-r-2 border-gray-300" style={{ width: '175px', minWidth: '175px', maxWidth: '175px' }}></th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50/50">Billable rate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50/50">Billable hrs AOY</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50/50">Billable total</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50/50 border-r-2 border-gray-300">Billable NOK AOY</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50/50">Billable rate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50/50">Client hours</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50/50">Billable amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50/50 border-r-2 border-gray-300">Logged hours</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-purple-50/50">This week</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-purple-50/50">Next week</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-purple-50/50">In 2 weeks</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-purple-50/50">In 3 weeks</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={13} className="px-6 py-12">
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
                    <p className="text-lg font-semibold text-slate-600">Loading team data...</p>
                  </div>
                </td>
              </tr>
            ) : teams.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-6 py-12">
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <p className="text-lg font-semibold text-slate-600">No team data available</p>
                    <p className="text-sm text-slate-500">Try adjusting your filters or check your data source</p>
                  </div>
                </td>
              </tr>
            ) : (
              teams.map((team) => {
                const resourceData = resourcePlanningData.find(rp => rp.team === team.name);
                const teamEmployees = employeesTable.filter((emp: any) => emp.team === team.name);

                return (
                  <tr key={team.id} className="hover:bg-slate-50/80 transition-colors duration-200 group">
                    <td className="px-6 py-4 text-xs font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 z-10 border-r-2 border-gray-300 transition-colors" style={{ width: '175px', minWidth: '175px', maxWidth: '175px' }}>
                      <div
                        className={`flex items-center space-x-3 min-w-0 ${onTeamSelect ? 'cursor-pointer hover:text-blue-600' : ''} transition-colors`}
                        onClick={() => onTeamSelect?.(team.name)}
                        title={onTeamSelect
                          ? (selectedTeams.length === 1 && selectedTeams[0] === team.name
                            ? "Klikk for å fjerne filter"
                            : "Klikk for å filtrere på dette teamet")
                          : undefined}
                      >
                        <div className={`w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          selectedTeams.length === 1 && selectedTeams[0] === team.name ? 'ring-2 ring-blue-400 ring-offset-1' : ''
                        }`}>
                          <span className="text-white text-xs font-bold">{team.name.charAt(0)}</span>
                        </div>
                        <span className={`truncate ${
                          selectedTeams.length === 1 && selectedTeams[0] === team.name ? 'text-blue-600 font-semibold' : ''
                        }`}>{team.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-blue-50/30 group-hover:bg-blue-50/50 transition-colors">
                      {(() => { const r = getTeamBudgetRate(teamEmployees); return r !== null ? `${r}%` : 'N/A'; })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-blue-50/30 group-hover:bg-blue-50/50 transition-colors">
                      {Math.round(teamEmployees.reduce((sum: number, emp: any) => sum + (emp.billableHrsAOY || 0), 0))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-blue-50/30 group-hover:bg-blue-50/50 transition-colors">
                      {Math.round(team.totalBudget).toLocaleString('no-NO')} kr
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-blue-50/30 group-hover:bg-blue-50/50 border-r-2 border-gray-300 transition-colors ${getBudgetVsActualColor(
                      teamEmployees.reduce((sum: number, emp: any) => sum + (emp.billableNOKAOY || 0), 0),
                      teamEmployees.reduce((sum: number, emp: any) => sum + (emp.billableAmountAOY || 0), 0)
                    )}`}>
                      {Math.round(teamEmployees.reduce((sum: number, emp: any) => sum + (emp.billableNOKAOY || 0), 0)).toLocaleString('no-NO')} kr
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-green-50/30 group-hover:bg-green-50/50 transition-colors ${getBillableRateAOYColor(teamEmployees)}`}>
                      {(() => {
                        const averageRate = averagePositiveValues(teamEmployees.map((emp: any) => emp.billableRateAOY));
                        return averageRate !== null ? `${Math.round(averageRate)}%` : 'N/A';
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-green-50/30 group-hover:bg-green-50/50 transition-colors">
                      {Math.round(teamEmployees.reduce((sum: number, emp: any) => sum + (emp.clientHoursAOY || 0), 0))}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-green-50/30 group-hover:bg-green-50/50 transition-colors ${getBudgetVsActualColor(
                      teamEmployees.reduce((sum: number, emp: any) => sum + (emp.billableNOKAOY || 0), 0),
                      teamEmployees.reduce((sum: number, emp: any) => sum + (emp.billableAmountAOY || 0), 0)
                    )}`}>
                      {Math.round(teamEmployees.reduce((sum: number, emp: any) => sum + (emp.billableAmountAOY || 0), 0)).toLocaleString('no-NO')} kr
                    </td>
                    {(() => {
                      const diff = teamEmployees.reduce((sum: number, emp: any) => sum + (emp.missingLoggedHours || 0), 0);
                      const budgetHoursAOY = teamEmployees.reduce((sum: number, emp: any) => sum + (emp.billableHrsAOY || 0), 0);
                      return (
                        <td className={`px-6 py-4 whitespace-nowrap text-xs font-semibold border-r-2 border-gray-300 transition-colors ${getLoggedHoursDiffClasses(diff, budgetHoursAOY)}`}>
                          {formatSignedHours(diff)}
                        </td>
                      );
                    })()}
                    <td className={`px-6 py-4 whitespace-nowrap text-xs font-medium bg-purple-50/30 group-hover:bg-purple-50/50 transition-colors ${getWeekColorClass(resourceData, 'billableRateThisWeek', teamEmployees)}`}>
                      {resourceData ? `${Math.round(resourceData.billableRateThisWeek)}%` : 'N/A'}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-xs font-medium bg-purple-50/30 group-hover:bg-purple-50/50 transition-colors ${getWeekColorClass(resourceData, 'billableRateNextWeek', teamEmployees)}`}>
                      {resourceData ? `${Math.round(resourceData.billableRateNextWeek)}%` : 'N/A'}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-xs font-medium bg-purple-50/30 group-hover:bg-purple-50/50 transition-colors ${getWeekColorClass(resourceData, 'billableRateIn2Weeks', teamEmployees)}`}>
                      {resourceData ? `${Math.round(resourceData.billableRateIn2Weeks)}%` : 'N/A'}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-xs font-medium bg-purple-50/30 group-hover:bg-purple-50/50 transition-colors ${getWeekColorClass(resourceData, 'billableRateIn3Weeks', teamEmployees)}`}>
                      {resourceData ? `${Math.round(resourceData.billableRateIn3Weeks)}%` : 'N/A'}
                    </td>
                  </tr>
                );
              })
            )}

            {/* Summary Row */}
            {!isLoading && teams.length > 0 && (
              <tr className="bg-gray-50 font-bold">
                <td className="px-6 py-4 text-xs font-bold text-gray-900 sticky left-0 bg-gray-50 z-10 border-r-2 border-gray-300" style={{ width: '175px', minWidth: '175px', maxWidth: '175px' }}>
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-8 h-8 bg-gradient-to-br from-slate-600 to-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">&Sigma;</span>
                    </div>
                    <span className="truncate">Total</span>
                  </div>
                </td>
                {(() => {
                  const allEmps = employeesTable.filter((emp: any) => teams.some(team => team.name === emp.team));
                  const avgBudgetRate = averagePositiveValues(allEmps.map((emp: any) => emp.billableRate));
                  const avgAOYRate = averagePositiveValues(allEmps.map((emp: any) => emp.billableRateAOY));
                  const billableNOKAOY = allEmps.reduce((sum: number, emp: any) => sum + (emp.billableNOKAOY || 0), 0);
                  const billableAmountAOY = allEmps.reduce((sum: number, emp: any) => sum + (emp.billableAmountAOY || 0), 0);
                  const diff = allEmps.reduce((sum: number, emp: any) => sum + (emp.missingLoggedHours || 0), 0);
                  const budgetHoursAOY = allEmps.reduce((sum: number, emp: any) => sum + (emp.billableHrsAOY || 0), 0);

                  const weekAvg = (field: 'billableRateThisWeek' | 'billableRateNextWeek' | 'billableRateIn2Weeks' | 'billableRateIn3Weeks') => {
                    const averageRate = averagePositiveValues(resourcePlanningData
                      .filter(rp => teams.some(team => team.name === rp.team))
                      .map(rp => rp[field])
                    );
                    return averageRate !== null ? `${Math.round(averageRate)}%` : 'N/A';
                  };

                  return (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-blue-100/50">
                        {avgBudgetRate !== null ? `${Math.round(avgBudgetRate)}%` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-blue-100/50">
                        {Math.round(allEmps.reduce((sum: number, emp: any) => sum + (emp.billableHrsAOY || 0), 0))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-blue-100/50">
                        {Math.round(teams.reduce((sum, team) => sum + team.totalBudget, 0)).toLocaleString('no-NO')} kr
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-blue-100/50 border-r-2 border-gray-300 ${getBudgetVsActualColorBold(billableNOKAOY, billableAmountAOY)}`}>
                        {Math.round(billableNOKAOY).toLocaleString('no-NO')} kr
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-green-100/50">
                        {avgAOYRate !== null ? `${Math.round(avgAOYRate)}%` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-green-100/50">
                        {Math.round(allEmps.reduce((sum: number, emp: any) => sum + (emp.clientHoursAOY || 0), 0))}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-green-100/50 ${getBudgetVsActualColorBold(billableNOKAOY, billableAmountAOY)}`}>
                        {Math.round(billableAmountAOY).toLocaleString('no-NO')} kr
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-xs font-bold border-r-2 border-gray-300 ${getLoggedHoursDiffClasses(diff, budgetHoursAOY)}`}>
                        {formatSignedHours(diff)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-purple-100/50">{weekAvg('billableRateThisWeek')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-purple-100/50">{weekAvg('billableRateNextWeek')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-purple-100/50">{weekAvg('billableRateIn2Weeks')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-purple-100/50">{weekAvg('billableRateIn3Weeks')}</td>
                    </>
                  );
                })()}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TeamKPIsWidget;
